/**
 * Simple event emitter for SDK events
 */

// Variadic ``never[]`` keeps registration contravariant-safe under
// strictFunctionTypes: any concretely-typed listener (e.g.
// ``(name: string) => void``) is assignable, while the emitter itself
// invokes handlers through an ``unknown[]`` cast in emit().
export type EventHandler = (...args: never[]) => void;

/**
 * Event emitter for SDK internal events
 */
export class EventEmitter {
  private events: Map<string, Set<EventHandler>>;

  constructor() {
    this.events = new Map();
  }

  /**
   * Register an event listener
   * @returns Unsubscribe function
   */
  on(event: string, handler: EventHandler): () => void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }

    this.events.get(event)!.add(handler);

    // Return unsubscribe function
    return () => this.off(event, handler);
  }

  /**
   * Remove an event listener
   */
  off(event: string, handler: EventHandler): void {
    const handlers = this.events.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Whether anyone is currently listening for `event`.
   *
   * Used by the redirect-return path to decide between emitting now and
   * retaining the outcome for the first listener to arrive: the constructor
   * resolves that outcome before `AlterConnect.create()` returns, so there is
   * a real window in which nobody can have subscribed yet.
   */
  hasListeners(event: string): boolean {
    const handlers = this.events.get(event);
    return handlers !== undefined && handlers.size > 0;
  }

  /**
   * Emit an event
   */
  emit(event: string, ...args: unknown[]): void {
    const handlers = this.events.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          (handler as (...emitted: unknown[]) => void)(...args);
        } catch (error) {
          console.error(
            `[Alter Connect] Error in event handler for '${event}':`,
            error,
          );
        }
      });
    }
  }

  /**
   * Remove all listeners for an event, or all listeners if no event specified
   */
  removeAllListeners(event?: string): void {
    if (event) {
      this.events.delete(event);
    } else {
      this.events.clear();
    }
  }
}
